import { coreTypes } from "@core/core-types.di";
import { ActivityAlertSettingsDto } from "@core/data/dto/activity-alert-settings.dto";
import type { Http } from "@core/data/infrastructures/http/http";
import { ActivityAlertSettingsMapper } from "@core/data/mappers/activity-alert-settings.mapper";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { ActivityAlertSettings } from "@core/domain/models/activity-alert-settings.model";
import { Either } from "@core/domain/types/either";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";

@injectable()
export class ActivityAlertSettingsDatasource {
    constructor(
        @inject(coreTypes.infrastructure.Http) private readonly http: Http,
        @inject(ActivityAlertSettingsMapper)
        private readonly mapper: ActivityAlertSettingsMapper,
    ) {}

    async fetchMy(): Promise<Either<FallbackError, ActivityAlertSettings>> {
        const result = await this.http.get<ActivityAlertSettingsDto>(
            "/activity_alert_settings/me/",
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => {
                const dto = plainToClass(
                    ActivityAlertSettingsDto,
                    response.data,
                );
                return this.mapper.map(dto) ?? ActivityAlertSettings.defaults();
            });
    }

    async updateMy(
        settings: ActivityAlertSettings,
    ): Promise<Either<FallbackError, ActivityAlertSettings>> {
        const body = this.mapper.toBody(settings);

        const result = await this.http.patch<
            ActivityAlertSettingsDto,
            typeof body
        >("/activity_alert_settings/me/", body);

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => {
                const dto = plainToClass(
                    ActivityAlertSettingsDto,
                    response.data,
                );
                return this.mapper.map(dto) ?? settings;
            });
    }
}
